import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FiPlus, FiEdit2, FiTrash2, FiSave, FiX, FiDollarSign } from 'react-icons/fi';
import './SettingPembiayaan.css';

const SettingPembiayaan = () => {
  const [biayaList, setBiayaList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [isCustomJenisBiaya, setIsCustomJenisBiaya] = useState(false);
  const [formData, setFormData] = useState({
    jenis_biaya: '',
    deskripsi: '',
    nominal: '',
    program_studi: '',
    angkatan: '',
    semester: '',
    jenis_pendaftaran: '',
    status: 'Aktif',
    is_wajib: true,
    metode_pembayaran: 'Sekali Bayar'
  });
  const [selectedBiaya, setSelectedBiaya] = useState(null);
  const [filterProdi, setFilterProdi] = useState('');
  const [filterAngkatan, setFilterAngkatan] = useState('');

  const programStudiOptions = [
    'Semua Program Studi',
    'Profesi Ners',
    'S1 Keperawatan',
    'S1 Kesehatan Masyarakat'
  ];

  const jenisBiayaOptions = [
    'Pendaftaran',
    'Orientasi Mahasiswa Baru',
    'Semester',
    'Kemahasiswaan',
    'Pembangunan',
    'Assessment Perolehan',
    'Praktikum',
    'KKN',
    'Skripsi',
    'Ujian',
    'Wisuda',
    'Lain-lain'
  ];

  const jenisPendaftaranOptions = [
    'Peserta Didik Baru',
    'RPL Transfer',
    'RPL Perolehan',
  ];

  const fetchBiaya = async () => {
    try {
      setLoading(true);
      const res = await axios.get('/api/finance/setting-pembiayaan');
      setBiayaList(res.data.data);
    } catch (error) {
      toast.error('Gagal mengambil data biaya');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBiaya();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOpenModal = (biaya = null) => {
    if (biaya) {
      setEditMode(true);
      setSelectedBiaya(biaya);
      setFormData({
        jenis_biaya: biaya.jenis_biaya,
        deskripsi: biaya.deskripsi || '',
        nominal: biaya.nominal,
        program_studi: biaya.program_studi,
        angkatan: biaya.angkatan || '',
        semester: biaya.semester || '',
        jenis_pendaftaran: biaya.jenis_pendaftaran || '',
        status: biaya.status,
        is_wajib: biaya.is_wajib,
        metode_pembayaran: biaya.metode_pembayaran
      });
      // Check if jenis_biaya is custom (not in predefined list)
      setIsCustomJenisBiaya(!jenisBiayaOptions.includes(biaya.jenis_biaya));
    } else {
      setEditMode(false);
      setSelectedBiaya(null);
      setIsCustomJenisBiaya(false);
      setFormData({
        jenis_biaya: '',
        deskripsi: '',
        nominal: '',
        program_studi: '',
        angkatan: '',
        semester: '',
        jenis_pendaftaran: '',
        status: 'Aktif',
        is_wajib: true,
        metode_pembayaran: 'Sekali Bayar'
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditMode(false);
    setSelectedBiaya(null);
    setIsCustomJenisBiaya(false);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleJenisBiayaChange = (e) => {
    const value = e.target.value;
    
    if (value === 'CUSTOM') {
      setIsCustomJenisBiaya(true);
      setFormData(prev => ({ ...prev, jenis_biaya: '' }));
    } else {
      setIsCustomJenisBiaya(false);
      setFormData(prev => ({ ...prev, jenis_biaya: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.jenis_biaya || !formData.nominal || !formData.program_studi) {
      toast.error('Mohon lengkapi data yang wajib diisi');
      return;
    }

    try {
      if (editMode) {
        await axios.put(`/api/finance/setting-pembiayaan/${selectedBiaya._id}`, formData);
        toast.success('Data biaya berhasil diupdate');
      } else {
        await axios.post('/api/finance/setting-pembiayaan', formData);
        toast.success('Data biaya berhasil ditambahkan');
      }
      
      handleCloseModal();
      fetchBiaya();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Terjadi kesalahan');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Apakah Anda yakin ingin menghapus data biaya ini?')) {
      try {
        await axios.delete(`/api/finance/setting-pembiayaan/${id}`);
        toast.success('Data biaya berhasil dihapus');
        fetchBiaya();
      } catch (error) {
        toast.error('Gagal menghapus data biaya');
      }
    }
  };

  const formatRupiah = (angka) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(angka);
  };

  const filteredBiaya = biayaList.filter(biaya => {
    const matchProdi = !filterProdi || filterProdi === 'Semua Program Studi' || biaya.program_studi === filterProdi;
    const matchAngkatan = !filterAngkatan || biaya.angkatan === filterAngkatan;
    return matchProdi && matchAngkatan;
  });

  const uniqueAngkatan = [...new Set(biayaList.map(b => b.angkatan).filter(Boolean))].sort().reverse();

  if (loading) {
    return <div className="spinner"></div>;
  }

  return (
    <div className="setting-pembiayaan-page">
      {/* Header */}
      <div className="modern-header">
        <div className="header-background">
          <div className="header-pattern"></div>
        </div>
        <div className="header-content">
          <div className="header-text">
            <div className="header-icon-wrapper">
              <FiDollarSign className="header-icon" />
            </div>
            <div>
              <h1>Setting Pembiayaan</h1>
              <p>Kelola jenis dan nominal biaya mahasiswa</p>
            </div>
          </div>
          <div className="header-actions">
            <button className="btn btn-glass" onClick={() => handleOpenModal()}>
              <FiPlus /> Tambah Biaya
            </button>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
            <FiDollarSign />
          </div>
          <div className="stat-details">
            <h3>{biayaList.length}</h3>
            <p>Total Jenis Biaya</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' }}>
            <FiDollarSign />
          </div>
          <div className="stat-details">
            <h3>{biayaList.filter(b => b.status === 'Aktif').length}</h3>
            <p>Biaya Aktif</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' }}>
            <FiDollarSign />
          </div>
          <div className="stat-details">
            <h3>{biayaList.filter(b => b.is_wajib).length}</h3>
            <p>Biaya Wajib</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="filters">
          <div className="search-box">
            <select 
              className="filter-select"
              value={filterProdi}
              onChange={(e) => setFilterProdi(e.target.value)}
            >
              <option value="">Semua Program Studi</option>
              {programStudiOptions.map(prodi => (
                <option key={prodi} value={prodi}>{prodi}</option>
              ))}
            </select>
          </div>
          <div className="search-box">
            <select 
              className="filter-select"
              value={filterAngkatan}
              onChange={(e) => setFilterAngkatan(e.target.value)}
            >
              <option value="">Semua Angkatan</option>
              {uniqueAngkatan.map(angkatan => (
                <option key={angkatan} value={angkatan}>{angkatan}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card">
        <div className="modern-table-container">
          <table className="modern-table">
            <thead>
              <tr>
                <th>No</th>
                <th>Jenis Biaya</th>
                <th>Deskripsi</th>
                <th>Nominal</th>
                <th>Program Studi</th>
                <th>Angkatan</th>
                <th>Semester</th>
                <th>Jenis Pendaftaran</th>
                <th>Metode</th>
                <th>Wajib</th>
                <th>Status</th>
                <th>Aksi</th>
              </tr>
            </thead>
            <tbody>
              {filteredBiaya.length === 0 ? (
                <tr>
                  <td colSpan="12" style={{ textAlign: 'center', padding: '40px' }}>
                    Tidak ada data biaya
                  </td>
                </tr>
              ) : (
                filteredBiaya.map((biaya, index) => (
                  <tr key={biaya._id}>
                    <td>{index + 1}</td>
                    <td><strong>{biaya.jenis_biaya}</strong></td>
                    <td>{biaya.deskripsi || '-'}</td>
                    <td><strong style={{ color: '#10b981' }}>{formatRupiah(biaya.nominal)}</strong></td>
                    <td>{biaya.program_studi}</td>
                    <td>{biaya.angkatan || '-'}</td>
                    <td>{biaya.semester || '-'}</td>
                    <td>{biaya.jenis_pendaftaran || 'Semua'}</td>
                    <td>{biaya.metode_pembayaran}</td>
                    <td>
                      {biaya.is_wajib ? (
                        <span className="badge badge-success">Wajib</span>
                      ) : (
                        <span className="badge badge-info">Opsional</span>
                      )}
                    </td>
                    <td>
                      <span className={`badge ${biaya.status === 'Aktif' ? 'badge-success' : 'badge-danger'}`}>
                        {biaya.status}
                      </span>
                    </td>
                    <td>
                      <div className="table-actions">
                        <button
                          className="table-btn table-btn-edit"
                          onClick={() => handleOpenModal(biaya)}
                          title="Edit"
                        >
                          <FiEdit2 />
                        </button>
                        <button
                          className="table-btn table-btn-delete"
                          onClick={() => handleDelete(biaya._id)}
                          title="Hapus"
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editMode ? 'Edit Biaya' : 'Tambah Biaya Baru'}</h3>
              <button className="modal-close" onClick={handleCloseModal}>&times;</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-grid">
                  <div className="form-group">
                    <label>Jenis Biaya <span className="required">*</span></label>
                    {isCustomJenisBiaya ? (
                      <div style={{ display: 'flex', gap: '10px' }}>
                        <input
                          type="text"
                          name="jenis_biaya"
                          value={formData.jenis_biaya}
                          onChange={handleChange}
                          className="form-control"
                          placeholder="Tulis jenis biaya"
                          required
                          autoFocus
                        />
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => {
                            setIsCustomJenisBiaya(false);
                            setFormData(prev => ({ ...prev, jenis_biaya: '' }));
                          }}
                          style={{ whiteSpace: 'nowrap' }}
                        >
                          Pilih dari List
                        </button>
                      </div>
                    ) : (
                      <select
                        name="jenis_biaya"
                        value={formData.jenis_biaya}
                        onChange={handleJenisBiayaChange}
                        className="form-control"
                        required
                      >
                        <option value="">Pilih Jenis Biaya</option>
                        {jenisBiayaOptions.map(jenis => (
                          <option key={jenis} value={jenis}>{jenis}</option>
                        ))}
                        <option value="CUSTOM">✏️ Tulis Sendiri...</option>
                      </select>
                    )}
                  </div>

                  <div className="form-group">
                    <label>Nominal <span className="required">*</span></label>
                    <input
                      type="number"
                      name="nominal"
                      value={formData.nominal}
                      onChange={handleChange}
                      className="form-control"
                      placeholder="Masukkan nominal"
                      required
                    />
                  </div>

                  <div className="form-group full-width">
                    <label>Deskripsi</label>
                    <textarea
                      name="deskripsi"
                      value={formData.deskripsi}
                      onChange={handleChange}
                      className="form-control"
                      rows="2"
                      placeholder="Deskripsi biaya (opsional)"
                    />
                  </div>

                  <div className="form-group">
                    <label>Program Studi <span className="required">*</span></label>
                    <select
                      name="program_studi"
                      value={formData.program_studi}
                      onChange={handleChange}
                      className="form-control"
                      required
                    >
                      <option value="">Pilih Program Studi</option>
                      {programStudiOptions.map(prodi => (
                        <option key={prodi} value={prodi}>{prodi}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Angkatan</label>
                    <input
                      type="text"
                      name="angkatan"
                      value={formData.angkatan}
                      onChange={handleChange}
                      className="form-control"
                      placeholder="Contoh: 2024"
                    />
                  </div>

                  <div className="form-group">
                    <label>Semester</label>
                    <input
                      type="text"
                      name="semester"
                      value={formData.semester}
                      onChange={handleChange}
                      className="form-control"
                      placeholder="Contoh: 1, 2, 3..."
                    />
                  </div>

                  <div className="form-group">
                    <label>Jenis Pendaftaran</label>
                    <select
                      name="jenis_pendaftaran"
                      value={formData.jenis_pendaftaran}
                      onChange={handleChange}
                      className="form-control"
                    >
                      <option value="">Semua Jenis</option>
                      {jenisPendaftaranOptions.map(jenis => (
                        <option key={jenis} value={jenis}>{jenis}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Metode Pembayaran</label>
                    <select
                      name="metode_pembayaran"
                      value={formData.metode_pembayaran}
                      onChange={handleChange}
                      className="form-control"
                    >
                      <option value="Sekali Bayar">Sekali Bayar</option>
                      <option value="Cicilan">Cicilan</option>
                      <option value="Per Semester">Per Semester</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Status</label>
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleChange}
                      className="form-control"
                    >
                      <option value="Aktif">Aktif</option>
                      <option value="Tidak Aktif">Tidak Aktif</option>
                    </select>
                  </div>

                  <div className="form-group full-width">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="is_wajib"
                        checked={formData.is_wajib}
                        onChange={handleChange}
                      />
                      <span>Biaya Wajib</span>
                    </label>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>
                  <FiX /> Batal
                </button>
                <button type="submit" className="btn btn-primary">
                  <FiSave /> {editMode ? 'Update' : 'Simpan'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingPembiayaan;
